// Angular Imports
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { filter, first, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { OpportunitiesAgentService } from '@amfam/opportunities';
import { policiesQuery, PolicySelectors, PolicySummarySelectors } from '@amfam/policy/data-access';
import {
  Policy,
  PolicyAnalytics,
  PolicyRole,
  RiskModel,
  Vehicle,
  VehicleRole
} from '@amfam/policy/models';
import { AddressPipe, PrettyPolicyNum } from '@amfam/policy/pipes';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import {
  AgentInfo,
  ApiStatus,
  Driver,
  PartyAddress,
  PolicyChangeRequestType
} from '@amfam/shared/models';
import { PrettyPhoneNumberPipe } from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
// My Account Imports
import { routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as fromPolicyChangeContainer from '../../+state';
import {
  addressChangeNotification,
  driverLicenseChangeANS,
  PolicyChangeRequestNotificationAction,
  PolicyChangeRequestSendAnalyticAction,
  PolicyChangeRequestSetCancelPathAction,
  vinChangeNotification
} from '../../+state/policy-change.actions';
import { AddressChange, ChangeRequest, DriverLicenseChange, RequestChange } from '../../models';

@Component({
  selector: 'app-plchangerequest',
  templateUrl: './plchangerequest.component.html',
  styleUrls: ['./plchangerequest.component.scss'],
  standalone: false
})
export class PlchangerequestComponent implements OnInit, AfterViewInit {
  public firstName: Observable<string>;
  public lastName: Observable<string>;
  public currentAddress: Observable<PartyAddress>;
  public policyList: Observable<Policy[]>;
  public propertyPolicies: Observable<Policy[]>;
  public policyErrorList: Array<ErrorObj> = [];
  public emailNotificationError: Observable<ApiStatus>;
  public policyError: Observable<boolean>;
  public pending: Observable<boolean>;
  newTemplate = this.featureflag.isEnabled('request_change_new_template');

  public agents$: Observable<AgentInfo[]> = this.opportunityAgentService.getAgents();
  public addressInfo$: Observable<Partial<RequestChange>>;
  public driverLicense$: Observable<Partial<RequestChange>>;
  public drivers$: Observable<Driver[]>;
  public risks$: Observable<RiskModel[]>;
  public years$: Observable<number[]> = this.store.select(vehicleQuery.selectYears);
  public isPartner$: Observable<boolean> = this.store.select(BrandSelectors.selectIsPartner);

  // Private variables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private dockingService: DockingBarService,
    private pageTitle: PageTitleService,
    private copyService: CopyService,
    public spinner: LoadingSpinnerService,
    private store: Store<fromRoot.RootState>,
    private opportunityAgentService: OpportunitiesAgentService,
    private router: Router,
    private featureflag: FeatureFlagService
  ) {}

  ngOnInit() {
    this.drivers$ = this.store.select(policiesQuery.selectVehiclePolicies).pipe(
      map(policies => {
        const drivers = [];
        policies.forEach(p => {
          const pni = this.getNameFromPolicyRoles(PolicyRole.PRIMARYNAMEDINSURED, p);
          const sni = this.getNameFromPolicyRoles(PolicyRole.SECONDARYNAMEDINSURED, p);
          if (pni && !this.checkCustomerExist(drivers, pni.customerId)) drivers.push(pni);
          if (sni && !this.checkCustomerExist(drivers, sni.customerId)) drivers.push(sni);
          p.vehicles.forEach(v => {
            const listedDrivers = this.getNameFromVehicleRoles(v, p);
            if (listedDrivers) {
              listedDrivers.forEach(ld => {
                if (!this.checkCustomerExist(drivers, ld.customerId)) drivers.push(ld);
              });
            }
          });
        });
        return drivers;
      })
    );
    // Start the subscription to loading indicator as the first thing when loading the component
    combineLatest(
      this.store.pipe(select(fromPolicyChangeContainer.selectPolicyChangePageLoading)),
      this.store.pipe(select(PolicySelectors.selectPoliciesLoading)),
      this.store.pipe(select(agentQuery.getAgentsLoading)),
      (changeLoading, policiesLoading, agentsLoading) =>
        changeLoading || policiesLoading || agentsLoading
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(loading => {
        loading ? this.spinner.start() : this.spinner.stop();
      });

    // Fetch the copy
    const pageTitle: string = this.copyService.getCopy('policy.policyChangeTitle');

    // Set the page title and docking bar
    this.pageTitle.set(pageTitle);
    this.dockingService.registerHeading(pageTitle);

    // Setup page async variables
    this.firstName = this.store.pipe(
      select(userQuery.selectFirstName),
      filter(name => !!name),
      take(1)
    );
    this.lastName = this.store.pipe(
      select(userQuery.selectLastName),
      filter(name => !!name),
      take(1)
    );
    this.currentAddress = this.store.pipe(
      select(userQuery.selectAddress),
      filter(address => !!address),
      take(1)
    );
    this.policyList = this.store.pipe(
      select(PolicySelectors.selectPolicies),
      filter(policies => !!policies.length),
      take(1),
      map(policies => policies.concat().sort(Policy.compare))
    );
    this.propertyPolicies = this.store.pipe(
      select(PolicySelectors.selectPropertyPolicies),
      filter(policies => !!policies.length),
      take(1)
    );

    this.addressInfo$ = this.store.select(policiesQuery.selectActivePolicies).pipe(
      withLatestFrom(
        this.store.select(userQuery.selectCustomerId),
        this.store.select(userQuery.selectPartnerId),
        this.store.select(userQuery.selectFirstName),
        this.store.select(userQuery.selectFirstAndLastName),
        this.store.select(userQuery.selectPrimaryEmail),
        this.store.select(userQuery.selectPhones),
        this.store.select(userQuery.selectAddress)
      ),
      first(),
      map(
        ([
          policies,
          customerId,
          partnerId,
          customerFirstName,
          customerName,
          email,
          phones,
          address
        ]) => {
          return {
            customerId,
            partnerId,
            policyNumbers: policies
              .map(p => new PrettyPolicyNum().transform(p.policyNumber))
              .join(', '),
            customerFirstName,
            customerName,
            emailAddress: email.emailAddress,
            phoneNumbers: phones
              .map(
                p =>
                  new PrettyPhoneNumberPipe().transform(p.tenDigitPhoneNumber) +
                  (p.extension ? `ext. ${p.extension}` : '')
              )
              .join(', '),
            mailingAddress: new AddressPipe().transform(address, 'long-singleline')
          };
        }
      )
    );
    this.risks$ = this.store
      .select(policiesQuery.selectVehicleRisks)
      .pipe(
        map(vehicles =>
          vehicles.filter(
            v =>
              v.vehicleTypeName === 'Car/Light Truck' ||
              v.vehicleTypeName === 'Private Passenger Auto'
          )
        )
      );

    this.driverLicense$ = this.addressInfo$.pipe(
      map(addressInfo => ({
        agentId: addressInfo.agentId,
        agentName: addressInfo.agentName,
        emailAddress: addressInfo.emailAddress,
        mailingAdrress: addressInfo.mailingAddress,
        customerFirstName: addressInfo.customerFirstName,
        customerId: addressInfo.customerId,
        phoneNumbers: addressInfo.phoneNumbers,
        policyNumbers: addressInfo.policyNumbers,
        customerName: addressInfo.customerName,
        partnerId: addressInfo.partnerId
      }))
    );
  }
  /**
   * @description: Call the analytics and error subscriptions after the page has been initialized and ready to view/interact.
   * This prevents the page from being blocked by the non essential analytics call.
   */
  ngAfterViewInit() {
    // Setup the loading indicator
    this.pending = this.store.pipe(select(fromPolicyChangeContainer.selectPolicyChangePageLoading));

    // Setup the error observable to be used in async pipe in the template when unable to send mail.
    (this.emailNotificationError = this.store.select(
      fromPolicyChangeContainer.selectPolicyChangePageError
    )),
      filter(error => !!error);

    // Setup the error observable to be used in async pipe in the template when polices doesnt load and no agents.
    this.policyError = merge(
      this.store.pipe(
        select(agentQuery.getAgentsError),
        filter(error => !!error),
        take(1)
      ),
      this.store.pipe(
        select(PolicySelectors.selectPolicyHasError),
        filter(error => !!error),
        take(1)
      ),
      this.store.pipe(
        select(PolicySummarySelectors.selectPolicySummariesHasError),
        filter(error => !!error),
        take(1)
      )
    ).pipe(
      filter(error => !!error),
      take(1)
    );

    setTimeout(() => {
      this.store.dispatch(
        PolicyChangeRequestSendAnalyticAction({
          payload: {
            pageAnalytic: PolicyAnalytics.policyChangeRequestPageAnalytic,
            eventAnalytic: PolicyAnalytics.policyChangeRequestEventAnalytic
          }
        })
      );
    });

    // Build the error list only when we have an error on the page.
    merge(this.emailNotificationError, this.policyError)
      .pipe(take(1))
      .subscribe(() => {
        // Get the error
        this.policyErrorList = new Array(this.getPoliciesError(this.copyService));
        this.spinner.stop();
      });

    this.store
      .pipe(
        select(routerQuery.getRouterPreviousStateUrl),
        filter(
          // Ignore the url if coming from changerequest or the changerequest confirmation page.
          url => url !== '/policies/changerequest' && url !== '/policies/changerequest/confirmation'
        ),
        take(1)
      )
      .subscribe(url =>
        this.store.dispatch(PolicyChangeRequestSetCancelPathAction({ payload: url }))
      );
  }

  /**
   * @description Function which fetches the error from the copy service and returns it in the error obj format.
   */
  private getPoliciesError(copyService: CopyService): ErrorObj {
    return {
      errorMessage: copyService.getCopy('policy.defaultErrorMessage'),
      errorLevel: ErrorLevels.HIGH
    };
  }

  public handleChangeRequest(changeRequest: ChangeRequest) {
    if (this.newTemplate) {
      switch (changeRequest.policyChangeType) {
        case PolicyChangeRequestType.ADDRESS_CHANGE:
          this.store.dispatch(addressChangeNotification({ payload: changeRequest }));
          break;
        case PolicyChangeRequestType.DRIVER_LICENSE_CHANGE:
          this.store.dispatch(driverLicenseChangeANS({ payload: changeRequest }));
          break;
        case PolicyChangeRequestType.VIN_CHANGE:
          this.store.dispatch(vinChangeNotification({ payload: changeRequest }));
          break;
      }
    } else this.store.dispatch(PolicyChangeRequestNotificationAction({ payload: changeRequest }));
  }

  public handleCloseEvent() {
    this.store
      .pipe(select(fromPolicyChangeContainer.getPolicyChangeCancelPath), take(1))
      .subscribe(path => this.router.navigateByUrl(path));
  }

  private getNameFromPolicyRoles(key: string, policy: Policy): Driver {
    if (!key || !policy) return null;
    const roles = policy.policyRoles;
    const role = roles?.find(r => r.roleType.toLowerCase() === key.toLowerCase());
    return role
      ? {
          customerId: role.customerId,
          name: role.name,
          productDescription: policy.productDescription
        }
      : null;
  }

  private getNameFromVehicleRoles(vehicle: Vehicle, policy: Policy): Driver[] {
    if (vehicle) {
      const roles = vehicle.vehicleRoles;
      return roles
        .filter(role =>
          [
            VehicleRole.ADDITIONALDRIVER.toLowerCase(),
            VehicleRole.PRIMARYDRIVER.toLowerCase(),
            VehicleRole.DRIVER.toLocaleLowerCase()
          ].includes(role.roleType.toLowerCase())
        )
        .map(role => ({
          customerId: role.customerId,
          name: role.name,
          productDescription: policy.productDescription
        }));
    }
    return null;
  }

  private checkCustomerExist(drivers: Driver[], customerId: string) {
    return drivers.map(d => d.customerId).includes(customerId);
  }
}
